import { Fragment, useCallback, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import infoSmall from '~/assets/icons/info_small.svg';
import setup from '~/assets/icons/setup.svg';
import star from '~/assets/icons/star.svg';
import trash from '~/assets/icons/trash.svg';
import user from '~/assets/icons/user.svg';
import { playSound, Sound } from '~/assets/sounds';
import { Text } from '~/components/ui/Text';
import { rage } from '~/rage';
import { useModal } from '../../hooks';
import {
	setSaveAbsoluteIndex,
	setSaveSelectedIndex,
	setSaveSelectedList,
	useLayoutDispatch,
} from '../../redux';
import { popups, popupText } from '../../views/Handling/Handling.model';
import { Divider } from '../Divider';
import { ISaveProps } from './Save.model';
import {
	StyledActiveButton,
	StyledButton,
	StyledButtonsContainer,
	StyledContainer,
	StyledDot,
	StyledHoverBackground,
	StyledInfoHintAuthorContainer,
	StyledInfoHintContainer,
	StyledInfoHintRowContainer,
	StyledMode,
	StyledPopupContainer,
	StyledPopupWrapper,
	StyledRemoveButton,
	StyledTextLabel,
} from './Save.styled';

const Save: React.FC<
	ISaveProps & {
		index: number;
		id: string;
		scrollOffset?: number;
		absoluteIndex: number;
	}
> = ({
	name,
	info,
	author,
	creationDate,
	isFavorite,
	selected,
	list,
	index,
	id,
	scrollOffset,
	mode,
	absoluteIndex,
}: ISaveProps & {
	index: number;
	id: string;
	scrollOffset?: number;
	absoluteIndex: number;
}) => {
	const dispatch = useLayoutDispatch();

	const [hover, setHover] = useState<boolean>(false);
	const [popupVisible, setPopupVisible] = useState<boolean>(false);
	const [infoHintVisible, setInfoHintVisible] = useState<boolean>(false);
	const [activePopup, setActivePopup] = useState<popups>(popups.Upload);
	const { handleSetupSaveModal, handleDeleteModal } = useModal();

	const handleShowSetupModal = useCallback(() => {
		dispatch(setSaveAbsoluteIndex(absoluteIndex));
		handleSetupSaveModal();
	}, [handleSetupSaveModal, absoluteIndex]);

	const handleShowDeleteModal = useCallback(() => {
		dispatch(setSaveAbsoluteIndex(absoluteIndex));
		handleDeleteModal();
	}, [handleDeleteModal, absoluteIndex]);

	const handlePopupShow = useCallback((popup: popups) => {
		setActivePopup(popup);
		setPopupVisible(true);
	}, []);

	const handleClick = useCallback(() => {
		dispatch(setSaveSelectedIndex(index));
		dispatch(setSaveSelectedList(list));
		dispatch(setSaveAbsoluteIndex(absoluteIndex));
		playSound(Sound.ClickButton);
	}, [index, list, absoluteIndex]);

	const handleSetFavorite = useCallback(() => {
		rage.handlingTuning
			.setPresetFavorite(absoluteIndex, !isFavorite)
			.catch((err) => rage.notifications.showException(err));
	}, [absoluteIndex, isFavorite]);

	return (
		<Fragment>
			<CSSTransition
				in={popupVisible}
				classNames="popup"
				timeout={500}
				unmountOnExit
			>
				<StyledPopupWrapper>
					<StyledPopupContainer
						$activePopup={activePopup}
						style={{ top: scrollOffset ? 50 - scrollOffset : 50 }}
					>
						<Text>{popupText[activePopup]}</Text>
					</StyledPopupContainer>
				</StyledPopupWrapper>
			</CSSTransition>
			<CSSTransition
				in={infoHintVisible}
				classNames={'hint'}
				timeout={500}
				unmountOnExit
			>
				<StyledPopupWrapper>
					<StyledInfoHintContainer
						style={{ top: scrollOffset ? 50 - scrollOffset : 50 }}
					>
						<StyledInfoHintRowContainer>
							<Text
								style={{ fontSize: '14px', fontWeight: 500, color: '#000000' }}
							>
								{name}
							</Text>
							<StyledMode $mode={mode}>
								<Text>{mode}</Text>
							</StyledMode>
						</StyledInfoHintRowContainer>
						<Divider
							$direction={'horizontal'}
							$top={0}
							$bottom={0}
							style={{ backgroundColor: '#000000' }}
						/>
						<Text
							style={{
								fontSize: '14px',
								fontWeight: 400,
								color: '#000000',
								opacity: '0.75',
							}}
						>
							{info}
						</Text>
						<StyledInfoHintRowContainer style={{ opacity: 0.5 }}>
							<StyledInfoHintAuthorContainer>
								<img src={user} />
								<Text>{author}</Text>
							</StyledInfoHintAuthorContainer>
							<Text>{creationDate}</Text>
						</StyledInfoHintRowContainer>
					</StyledInfoHintContainer>
				</StyledPopupWrapper>
			</CSSTransition>
			<StyledContainer
				$selected={selected}
				onMouseEnter={() => {
					setHover(true);
					playSound(Sound.LinkCursor);
				}}
				onMouseLeave={() => setHover(false)}
				onClick={handleClick}
				id={id}
			>
				<StyledHoverBackground />
				<StyledDot />
				<StyledTextLabel $hover={hover} $selected={selected}>
					{name}
				</StyledTextLabel>
				<StyledButtonsContainer>
					{/* <StyledActiveButton */}
					{/* 	$active={isNetwork} */}
					{/* 	onMouseEnter={() => handlePopupShow(popups.Upload)} */}
					{/* 	onMouseLeave={() => setPopupVisible(false)} */}
					{/* > */}
					{/* 	<img src={cloud} /> */}
					{/* </StyledActiveButton> */}
					<StyledActiveButton
						$active={isFavorite}
						onMouseEnter={() => {
							handlePopupShow(popups.Favorite);
							playSound(Sound.LinkCursor);
						}}
						onMouseLeave={() => setPopupVisible(false)}
						onClick={() => {
							handleSetFavorite();
							playSound(Sound.ClickButton);
						}}
					>
						<img src={star} width={14} height={14} />
					</StyledActiveButton>
					<StyledButton
						onMouseEnter={() => {
							setInfoHintVisible(true);
							playSound(Sound.LinkCursor);
						}}
						onMouseLeave={() => setInfoHintVisible(false)}
					>
						<img src={infoSmall} />
					</StyledButton>
					<StyledButton
						onClick={() => {
							handleShowSetupModal();
							playSound(Sound.ClickButton);
						}}
						onMouseEnter={() => {
							handlePopupShow(popups.Setup);
							playSound(Sound.LinkCursor);
						}}
						onMouseLeave={() => setPopupVisible(false)}
					>
						<img src={setup} />
					</StyledButton>
					<StyledRemoveButton
						onMouseEnter={() => {
							handlePopupShow(popups.Delete);
							playSound(Sound.LinkCursor);
						}}
						onMouseLeave={() => setPopupVisible(false)}
						onClick={() => {
							handleShowDeleteModal();
							playSound(Sound.ClickButton);
						}}
					>
						<img src={trash} />
					</StyledRemoveButton>
				</StyledButtonsContainer>
			</StyledContainer>
			<Divider $direction={'horizontal'} $top={0} $bottom={0} />
		</Fragment>
	);
};

export { Save };
